import React, { useEffect, useState } from "react";
import { useMutation, gql } from "@apollo/client";

// Editor for OUT_OF_HOURS_BONUS_RULES: extra pay for a booking that starts
// before or after office hours. Only one tier pays per booking - the
// earliest "before" it starts ahead of, or the latest "from" it starts at or
// after (see computeOutOfHoursBonus in the backend's payout.util.js).

const KEY = "OUT_OF_HOURS_BONUS_RULES";

const UPDATE_SYSTEM_CONFIG = gql`
  mutation UpdateOutOfHoursBonus($key: String!, $valueJson: JSON!) {
    updateSystemConfig(key: $key, valueJson: $valueJson) {
      status
      message
    }
  }
`;

const toRows = (value) =>
  (Array.isArray(value) ? value : []).map((r) => ({
    when: r.from ? "from" : "before",
    time: r.from || r.before || "",
    type: String(r.type || "FIXED").toUpperCase(),
    amount: r.amount ?? "",
  }));

const describe = (row) => {
  const amount =
    row.type === "PERCENT" ? `${row.amount || 0}% of booking price` : `₹${row.amount || 0}`;
  return row.when === "before"
    ? `Starts before ${row.time || "--:--"}: ${amount}`
    : `Starts at or after ${row.time || "--:--"}: ${amount}`;
};

const OutOfHoursBonusEditor = ({ configs, onSaved }) => {
  const current = configs.find((c) => c.key === KEY);
  const [rows, setRows] = useState(() => toRows(current?.valueJson));
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [save] = useMutation(UPDATE_SYSTEM_CONFIG);

  useEffect(() => {
    setRows(toRows(current?.valueJson));
  }, [current?.valueJson]);

  const update = (i, key, value) =>
    setRows((rs) => rs.map((r, j) => (j === i ? { ...r, [key]: value } : r)));

  const handleSave = async () => {
    setMessage("");
    const bad = rows.find((r) => !/^\d{2}:\d{2}$/.test(r.time) || r.amount === "");
    if (bad) {
      setMessage("Every tier needs a time (HH:MM) and an amount.");
      return;
    }
    setSaving(true);
    try {
      const value = rows.map((r) => ({
        [r.when]: r.time,
        type: r.type,
        amount: Number(r.amount),
      }));
      const res = await save({ variables: { key: KEY, valueJson: value } });
      if (res.data?.updateSystemConfig?.status !== "SUCCESS") {
        throw new Error(res.data?.updateSystemConfig?.message || "Save failed");
      }
      setMessage("Saved. Applies to bookings completed from now on.");
      onSaved();
    } catch (err) {
      setMessage(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border rounded-lg mb-6">
      <div className="p-4 border-b bg-gray-50">
        <h3 className="font-bold text-gray-800">Out-of-hours bonus</h3>
        <p className="text-xs text-gray-500 mt-1">
          Extra pay for a booking that starts early or late, by its booked start time (IST). One
          tier per booking: a 6:30 AM booking gets the "before 07:00" tier, not "before 09:00" as
          well. With no tiers, the old flat COMMISSION_EARLY_LATE_BONUS rule applies.
        </p>
      </div>
      <div className="p-4 space-y-2">
        {rows.length === 0 && (
          <p className="text-sm text-gray-500">No tiers - the old flat rule is in use.</p>
        )}
        {rows.map((row, i) => (
          <div key={i} className="flex items-center gap-2 text-sm">
            <select
              className="border p-1.5 rounded bg-white"
              value={row.when}
              onChange={(e) => update(i, "when", e.target.value)}
            >
              <option value="before">Starts before</option>
              <option value="from">Starts at or after</option>
            </select>
            <input
              type="time"
              className="border p-1.5 rounded"
              value={row.time}
              onChange={(e) => update(i, "time", e.target.value)}
            />
            <select
              className="border p-1.5 rounded bg-white"
              value={row.type}
              onChange={(e) => update(i, "type", e.target.value)}
            >
              <option value="FIXED">Fixed ₹</option>
              <option value="PERCENT">% of booking price</option>
            </select>
            <input
              type="number"
              min="0"
              step="any"
              className="border p-1.5 rounded w-24"
              value={row.amount}
              onChange={(e) => update(i, "amount", e.target.value)}
            />
            <span className="text-xs text-gray-500 flex-1">{describe(row)}</span>
            <button
              onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))}
              className="text-red-600 text-xs px-2"
            >
              Remove
            </button>
          </div>
        ))}
        <div className="flex items-center gap-3 pt-2">
          <button
            onClick={() =>
              setRows((rs) => [...rs, { when: "before", time: "", type: "FIXED", amount: "" }])
            }
            className="text-sm px-3 py-1.5 border rounded bg-white hover:bg-gray-100"
          >
            + Tier
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="text-sm px-3 py-1.5 rounded bg-blue-600 text-white disabled:opacity-50"
          >
            Save tiers
          </button>
          {message && <span className="text-sm text-gray-600">{message}</span>}
        </div>
      </div>
    </div>
  );
};

export default OutOfHoursBonusEditor;
